import { IUserLeaderboardViewModel } from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import WarningIcon from '../assets/icons/warning-circle.svg';
import { TranslateProp } from '../common-types';
import {
  Button, ButtonSize, ButtonTypes, Icon,
} from '../components';
import { IAppStore } from '../HomeWorkouts';
import Theme from '../theme';
import {
  closeOptOutLeaderboardModal,
  confirmOptOutOfLeaderboard as confirmOptOutOfLeaderboardAction,
} from '../Tracking/actions';
import { defaultScale, UserDataConsumer } from '../utils';
import { createUserServiceClient } from '../utils/apiClientFactory';

interface IProps {
  isLeaderboardOptOutModalVisible: boolean;
  isOptingOutOfLeaderboard: boolean;
  t: TranslateProp;
  closeModal: () => void;
  optOutOfLeaderboard: (leaderboard: IUserLeaderboardViewModel, optOutFromDashboard: boolean) => void;
  leaderboard: IUserLeaderboardViewModel;
  navigation: any;
}

const LeaderboardOptOutScreen: FunctionComponent<IProps> = ({
  isLeaderboardOptOutModalVisible,
  isOptingOutOfLeaderboard,
  t,
  closeModal,
  optOutOfLeaderboard,
  leaderboard,
  navigation,
}) => {
  if (!isLeaderboardOptOutModalVisible || !leaderboard) {
    return <></>;
  }

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <View style={styles.iconContainer}>
          <WarningIcon color={Theme.PRIMARY} />
        </View>
        <Text style={styles.header}>{t('tracking:confirmOptOutText', { name: leaderboard.name })}</Text>
        <Text style={styles.subHeader}>{t('tracking:optOutExplanation')}</Text>
      </View>
      <View style={styles.buttonContainer}>
        <View>
          <Button
            title={t('tracking:confirmOptOut')}
            buttonType={ButtonTypes.OUTLINE}
            size={ButtonSize.MEDIUM}
            isLoading={isOptingOutOfLeaderboard}
            onPress={() => optOutOfLeaderboard(leaderboard, navigation)}
          />
        </View>
        <View style={styles.outlineButtonContainer}>
          <Button
            title={t('tracking:cancelOptOut')}
            buttonType={ButtonTypes.NORMAL}
            size={ButtonSize.MEDIUM}
            overrideButtonTextStyle={{ color: Theme.WHITE }}
            fontColor={Theme.WHITE}
            onPress={closeModal}
            disabled={isOptingOutOfLeaderboard}
          />
        </View>
      </View>
    </View>
  );
};

const mapStateToProps = (state: IAppStore) => ({
  isLeaderboardOptOutModalVisible: state.tracking.view.isLeaderboardOptOutModalVisible,
  isOptingOutOfLeaderboard: state.tracking.view.isOptingOutOfLeaderboard,
  leaderboard: state.tracking.data.leaderboardToOptOutOf,
});

const mapDispatchToProps = (dispatch) => {
  const userServiceClient = createUserServiceClient();

  return {
    closeModal: () => dispatch(closeOptOutLeaderboardModal()),
    optOutOfLeaderboard: (leaderboard: IUserLeaderboardViewModel, navigation: any) =>
      dispatch(confirmOptOutOfLeaderboardAction(userServiceClient, leaderboard, navigation)),
  };
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'space-between',
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'center',
    alignSelf: 'center',
  },
  header: {
    ...Theme.textStyle.bodyCopyBold,
    marginTop: defaultScale(20),
  },
  subHeader: {
    marginTop: defaultScale(5),
    ...Theme.textStyle.bodyCopyReg,
  },
  iconContainer: {
    alignSelf: 'center',
  },
  buttonContainer: {
    marginBottom: defaultScale(20),
  },
  outlineButtonContainer: {
    marginTop: defaultScale(10),
  },
});

export const LeaderboardOptOut = translate(
  ['dashboard', 'tracking'],
  { wait: true },
)(UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(LeaderboardOptOutScreen)));
